import type { CancellationFeedback } from "components/MyAccount/MyMembership/active-membership/shared/types"
import { getCurrency } from "data/tracking/segment/helpers"
import dayjs from "dayjs"
import { getMemberships, getVariants } from "lib/api"
import { getLocale } from "lib/apolloClient"
import { subscribe } from "@shared/utils.events"
import { userVar } from "lib/user"
import type { SegmentProductAddedOrRemovedEvent } from "tracking"
import {
  getMembershipLocationTypeSegment,
  setMembershipLocationTypeSegment,
  ProductAddRemoveLocationType,
} from "tracking/utils/memberships"
import {
  getSegmentPurchaseType,
  ProductPriceType,
} from "tracking/utils/purchaseType"
import { getUrlFromWindow } from "tracking/utils/url"
import type { ItemInput, ItemType, SubscriptionType } from "types"
import { getPDPUrl } from "utils/getPDPUrl"
import { siteUrl } from "utils/getSiteUrl"
import { useSegment } from "./useSegment"
import { pushEventIdToGtmContext } from "utils/gtm/utils"
import { getTrackingEventId } from "lib/api/utils/trackingEventId"
import type { Common } from "@beautypie/segment"

// Passing nothing gives you the current date
const toSegmentDate = (date?: string): string =>
  dayjs(date).format("YYYY-MM-DD")

type MembershipTrackingEvent = {
  customerNumber: string
  subscription: SubscriptionType
  priorActiveSku: string
  flow: string
}

export const useTrackAPIEvents = (): (() => void) => {
  const { track } = useSegment()
  const locale = getLocale()
  const user = userVar()
  const eventId = getTrackingEventId()

  return () => {
    interface HandleAddRemoveBagPayload {
      items: ItemInput[]
      event_id: string
      locationType?: string
      locationTypeDetails?: string
    }

    const handleAddRemoveBag = async (
      payload: HandleAddRemoveBagPayload,
      event: "Product Added" | "Product Removed"
    ) => {
      const {
        items = [],
        event_id,
        locationType = "Page",
        locationTypeDetails = "",
      } = payload
      const purchaseType = getSegmentPurchaseType()

      if (!items?.length) return

      items.forEach(async (item) => {
        let additionalProperties
        if (item.membershipId || item.variantSku) {
          if (item.membershipId) {
            const result = await getMemberships([item.membershipId])
            if (!result) return

            if (locationType === "MINI_BAG") {
              setMembershipLocationTypeSegment(
                ProductAddRemoveLocationType.MiniBag
              )
            }

            const [{ name, price, value }] = result
            additionalProperties = {
              sku: value,
              name,
              brand: "Member",
              variant: "",
              price,
              typical_price: price,
              members_price: price,
              url: getUrlFromWindow(),
              location_type: getMembershipLocationTypeSegment(),
              location_type_details: locationTypeDetails,
            }
          }
          if (item.variantSku) {
            const [variant] = await getVariants([item.variantSku])

            if (!variant) return

            const { sku, pricing, pricingDetails, title, brandName, subTitle } =
              variant

            const displayedMembersPrice = pricingDetails?.primary?.raw
            const displayedNonMembersPrice = pricingDetails?.secondary?.raw

            additionalProperties = {
              sku,
              name: title,
              brand: brandName,
              variant: subTitle || "",
              price:
                purchaseType === ProductPriceType.MemberPrice
                  ? displayedMembersPrice
                  : displayedNonMembersPrice,
              members_price: displayedMembersPrice,
              typical_price: pricing.typical, //Need to confirm what this is used to track before changing
              url: `${siteUrl}${getPDPUrl(variant, locale, true)}`,
              location_type: locationType,
              location_type_details: locationTypeDetails,
            }
          }

          const data: SegmentProductAddedOrRemovedEvent = {
            event,
            properties: {
              currency: getCurrency(),
              product_price_type: purchaseType,
              quantity: item.quantity,
              ...additionalProperties,
            },
          }
          pushEventIdToGtmContext(eventId)
          track(data, { event_id: event_id || eventId } as Partial<
            Common["context"]
          >)
        }
      })
    }

    subscribe("addToBag", (payload) => {
      handleAddRemoveBag(
        payload as unknown as HandleAddRemoveBagPayload,
        "Product Added"
      )
    })

    subscribe("removeFromBag", (payload) => {
      handleAddRemoveBag(
        payload as unknown as HandleAddRemoveBagPayload,
        "Product Removed"
      )
    })

    type HandleAddRemoveWishlistPayload = {
      context?: Record<string, unknown>
      item: ItemType
    }

    const handleAddRemoveWishlist = async (
      payload: HandleAddRemoveWishlistPayload,
      event: "Product Added To Wishlist" | "Product Removed From Wishlist"
    ) => {
      const {
        context,
        item: { variant },
      } = payload

      if (!variant) {
        return
      }

      const purchaseType = getSegmentPurchaseType()

      const {
        sku,
        pricing: { members, typical },
        title: name,
        brandName: brand,
        subTitle,
      } = variant

      track({
        event,
        properties: {
          sku,
          name,
          brand,
          variant: subTitle || "",
          currency: locale === "en-GB" ? "GBP" : "USD",
          price: user?.isMember || user?.hasMembershipInBag ? members : typical,
          members_price: members,
          typical_price: typical,
          url: `${siteUrl}${getPDPUrl(variant, locale)}`,
          location_type: context?.location ? `${context.location}` : "",
          product_price_type: purchaseType,
          location_type_details: context?.locationDetails
            ? `${context.locationDetails}`
            : "",
        },
      })
    }

    subscribe("addToWishlist", async (payload) => {
      handleAddRemoveWishlist(
        payload as unknown as HandleAddRemoveWishlistPayload,
        "Product Added To Wishlist"
      )
    })

    subscribe("removeFromWishlist", async (payload) => {
      handleAddRemoveWishlist(
        payload as unknown as HandleAddRemoveWishlistPayload,
        "Product Removed From Wishlist"
      )
    })

    type HandleMembershipCancelledPayload = MembershipTrackingEvent & {
      cancellationFeedback: CancellationFeedback
    }

    const handleMembershipCancelled = (
      payload: HandleMembershipCancelledPayload
    ) => {
      const { subscription, cancellationFeedback } = payload

      const products = [
        {
          product_sku: subscription.sku,
          product_name: subscription.name,
          product_price: (subscription.billing?.centAmount ?? 0) / 100,
        },
      ]

      track({
        event: "Order Cancelled",
        properties: {
          is_membership_cancelled: !!subscription?.dateCancelled,
          membership_id: subscription.id,
          revenue: subscription.billing?.centAmount ?? 0,
          currency: subscription.billing?.currency ?? "",
          products,
          membership_start_date: subscription.dateStarted,
          reason:
            cancellationFeedback.reasonFreeText1 ||
            cancellationFeedback.reasonCode1 ||
            "",
          reason_feedback:
            cancellationFeedback.reasonFreeText2 ||
            cancellationFeedback.reasonCode2 ||
            "",
        },
      })
    }

    subscribe("membershipCancelled", (payload) => {
      handleMembershipCancelled(
        payload as unknown as HandleMembershipCancelledPayload
      )
    })

    const handleMembershipExtended = ({
      customerNumber,
      subscription,
    }: MembershipTrackingEvent) => {
      track({
        event: "Membership Extended",
        properties: {
          billing_period: subscription.billing?.billingPeriod.toString() ?? "",
          billing_period_unit: subscription.billing?.frequency ?? "",
          currency: subscription.billing?.currency ?? "",
          member_id: customerNumber,
          membership_expiry_date: toSegmentDate(
            subscription.billing?.nextPaymentDate ?? ""
          ),
          membership_extension_date: toSegmentDate(subscription.dateUpdated),
          membership_id: subscription.id,
          membership_name: subscription.name,
          membership_start_date: toSegmentDate(subscription.dateStarted ?? ""),
          membership_status: subscription.status,
          price: (subscription.billing?.centAmount ?? 0) / 100,
          sku: subscription.sku,
        },
      })
    }

    subscribe("membershipExtended", (payload) => {
      handleMembershipExtended(payload as unknown as MembershipTrackingEvent)
    })

    const handleMembershipUpgraded = ({
      customerNumber,
      subscription,
      priorActiveSku,
      flow,
    }: MembershipTrackingEvent) => {
      track({
        event: "Membership Upgraded",
        properties: {
          currency: subscription.billing?.currency ?? "",
          member_id: customerNumber,
          membership_expiry_date: toSegmentDate(
            subscription.billing?.nextPaymentDate ?? ""
          ),
          membership_id: subscription.id,
          membership_name: subscription.name,
          membership_start_date: toSegmentDate(subscription.dateStarted),
          membership_status: subscription.status,
          price: (subscription.billing?.centAmount ?? 0) / 100,
          sku: subscription.sku,
          prior_active_sku: priorActiveSku,
          flow,
        },
      })
    }

    subscribe("membershipUpgraded", (payload) => {
      handleMembershipUpgraded(payload as unknown as MembershipTrackingEvent)
    })
  }
}
